import { inject, Injectable, Renderer2 } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { LocalStorage } from '../helpers/localstorage.helper';
import { ToastrService } from 'ngx-toastr';
import { BaseService } from './base.service';
import { AppDataService } from './app-data.service';

@Injectable({
    providedIn: 'root'
})
export class ThemeService extends BaseService {
    // renderer = inject(Renderer2)

    public closeEmitter: Subject<any> = new Subject<any>();
    public saveEmitter: Subject<any> = new Subject<any>();
    public readonly _toaster: ToastrService = inject(ToastrService);

    private _initialDarkModeValue: boolean = false;
    public themeData: any = {};
    public defaultData: any = {};
    public showThemeDialog: boolean = false;
    public logoChanged: boolean = false;
    public previewLoginPage: boolean = false;

    private _darkMode = new BehaviorSubject<boolean>(this._initialDarkModeValue);

    isDarkMode = this._darkMode.asObservable();
    constructor(public _appData : AppDataService) {
        super();
        const mediaQuery: MediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');

        const storedTheme = LocalStorage.get('isDarkMode');
        if (storedTheme !== null) {
            this._initialDarkModeValue = storedTheme;
            this.toggleDarkMode(this._initialDarkModeValue);
        } else {
            this._initialDarkModeValue = mediaQuery.matches
        }

    }

    toggleDarkMode(isDarkMode: boolean = this._initialDarkModeValue): void {
        LocalStorage.set('isDarkMode', isDarkMode);
        this._darkMode.next(isDarkMode);
        if (isDarkMode) {
            // this.renderer.addClass(document.body, 'dark-theme');
            document.body.classList.add('dark-theme')
        }
        else {
            // this.renderer.removeClass(document.body, 'dark-theme');
            document.body.classList.remove('dark-theme')
        }
    }

    saveBrandingSetting() {
        this.showThemeDialog = false;
        let loginLogoObj: { LightImage: any, DarkImage: any } = {
            LightImage: this.themeData.LogoLogin,
            DarkImage: this.themeData.darkLogoLogin
        };

        let systemImageObj: { LightImage: any, DarkImage: any } = {
            LightImage: this.themeData.logoImagePath,
            DarkImage: this.themeData.logoImageDark
        };

        let brandingDto = {
            ApplicationThemeId: this.themeData.ApplicationThemeId,
            FontId: this.themeData.FontId,
            Mode: 0,
            tagStylingSettings: this.themeData.tagStylingSettings,
            ApplicationBrandingSettingsContainer: {
                SystemName: this.themeData.SystemName,
                SystemLongName: this.themeData.SystemLongName,
                ClientName: this.themeData.ClientName,
                CopyRight: this.themeData.CopyRight,
                LogoLogin: loginLogoObj,
                ExcelLogoImagePath: '',
                LogoStickerPath: '',
                LogoFavicon: this.themeData.LogoFavicon,
                logoImagePath: systemImageObj,
                LoaderLogoPath: this.themeData.LoaderLogoPath,
                SystemBackGroundLogoPath: this.themeData.SystemBackGroundLogoPath,
                DashboardBackground :''

            }
        };
        return this.saveBrandingSettingApi(brandingDto)

    }

    saveBrandingSettingApi(applicationBrandingSettingsAndThemes: any): Observable<any> {
        return super.request("AppBrandingSettings/SaveBrandingSetting", applicationBrandingSettingsAndThemes, "post");
    }

    resetBrandingSettings() {
        return super.request("AppBrandingSettings/ResetBrnadingSettings", {} , "get");
    }
    getLoader(loader:any) {
        let loaderData = this.themeData.loader || loader;


        try {
            let loaderJson: any;

            if (typeof loaderData === 'string') {
                if (loaderData?.startsWith('data:')) {
                    // Remove prefix and decode
                    const base64Content = loaderData.replace(/^data:.*;base64,?/, '').trim();
                    const decoded = atob(base64Content);
                    loaderJson = JSON.parse(decoded);
                } else {

                    if (loaderData.startsWith('"'))
                        loaderData = loaderData.replaceAll('"', '')//this.removeFirstAndLast(loaderData);

                    const decoded = this.isBase64(loaderData) ? atob(loaderData.trim()) : loaderData;
                    loaderJson = JSON.parse(decoded);
                }
            } else {
                loaderJson = loaderData;
            }

            return {
                animationData: loaderJson,
                loop: true,
                autoplay: true
            };

        } catch (e) {
            console.error("Failed to parse LoaderLogoPath JSON", e);
            throw e;
        }


    }

    isBase64(str: string) {
        try {
            return btoa(atob(str)) === str;
        } catch (err) {
            return false;
        }
    }

    generateRootTheme(selectedThemeColor: any, fontFamily: string, fontSize: string = '12px', spaceBase: string = '3px') {
        if (selectedThemeColor == undefined)
            selectedThemeColor = this._appData.configurations.systemThemes.RootTheme.baseColor;
        const brandColors = generateBrandColors(selectedThemeColor);

        const styleTag = document.createElement('style');
        styleTag.setAttribute('data-source', 'brand-colors');
        const colors = brandColors.map((obj) => {
            const [key, value] = Object.entries(obj)[0];
            return `  ${key}: ${value};`;
        }).join('\n');

        styleTag.textContent = `
        :root{
            --font-family: ${fontFamily};
            --font-size-base: ${fontSize}px;
            --space-base: 3px;
            ${colors }
        }`;

        const existing = document.querySelector('style[data-source="brand-colors"]');
        if (existing) {
            existing.remove();
        }
        document.head.appendChild(styleTag);


    }
}

function hexToRgb(hex: string) {
    hex = hex.replace("#", "");
    if (hex.length === 3) {
        hex = hex.split("").map(c => c + c).join("");
    }
    const bigint = parseInt(hex, 16);
    return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255
    };
}

function rgbToHex(r: number, g: any, b: any) {
    return "#" + [r, g, b]
        .map(x => {
            const hex = x?.toString(16);
            return hex?.length === 1 ? "0" + hex : hex;
        })
        .join("")
        .toUpperCase();
}

function addAlpha(hex: string, alpha: number) {
    const alphaHex = Math.round(alpha * 255).toString(16).padStart(2, "0").toUpperCase();
    return hex + alphaHex;
}

function lighten({ r, g, b }: { r: number; g: number; b: number; }, percent: number) {
    return {
        r: Math.min(255, Math.round(r + (255 - r) * percent)),
        g: Math.min(255, Math.round(g + (255 - g) * percent)),
        b: Math.min(255, Math.round(b + (255 - b) * percent)),
    };
}

function darken({ r, g, b }: { r: number; g: number; b: number; }, percent: number) {
    return {
        r: Math.max(0, Math.round(r * (1 - percent))),
        g: Math.max(0, Math.round(g * (1 - percent))),
        b: Math.max(0, Math.round(b * (1 - percent))),
    };
}

function generateBrandColors(baseHex: string) {
    const baseRgb = hexToRgb(baseHex);

    const colors = [
        { "--color-brand-900": rgbToHex(...Object.values(darken(baseRgb, 0.55)) as [number, number, number]) },
        { "--color-brand-800": rgbToHex(...Object.values(darken(baseRgb, 0.45)) as [number, number, number]) },
        { "--color-brand-700": rgbToHex(...Object.values(darken(baseRgb, 0.35)) as [number, number, number]) },
        { "--color-brand-612": addAlpha(baseHex.toUpperCase(), 0.12) },
        { "--color-brand-608": addAlpha(baseHex.toUpperCase(), 0.08) },
        { "--color-brand-602": addAlpha(baseHex.toUpperCase(), 0.01) },
        { "--color-brand-600": baseHex.toUpperCase() },
        { "--color-brand-500": rgbToHex(...Object.values(lighten(baseRgb, 0.1)) as [number, number, number]) },
        { "--color-brand-400": rgbToHex(...Object.values(lighten(baseRgb, 0.2)) as [number, number, number]) },
        { "--color-brand-300": rgbToHex(...Object.values(lighten(baseRgb, 0.4)) as [number, number, number]) },
        { "--color-brand-200": rgbToHex(...Object.values(lighten(baseRgb, 0.6)) as [number, number, number]) },
        { "--color-brand-100": rgbToHex(...Object.values(lighten(baseRgb, 0.80)) as [number, number, number]) },
        { "--color-brand-50": rgbToHex(...Object.values(lighten(baseRgb, 0.85)) as [number, number, number]) },
        { "--color-brand-10": rgbToHex(...Object.values(lighten(baseRgb, 0.95)) as [number, number, number]) },
    ];

    return colors;
}
